import { Action, AuthSubAgentConfig } from '@/shared/types/action';
import { AuthConfig, Node } from '@/shared/types/node';

export interface AuthSubAgentLocation {
  dialog_id: string;
  dialog_name?: string;
  node_id?: string;
  action_id: string;
}

export interface WalkedAuthConfig {
  config: AuthConfig;
  location: AuthSubAgentLocation;
}

interface MinimalDialog {
  dialog_id: string;
  name?: string;
  nodes?: Node[];
}

/**
 * Iterate every `sub_agent` action carrying an auth config inside a brain's
 * dialogs. Mirrors the orchestrator's `walkAuthSubAgentConfigs` helper. The
 * `type: 'auth'` discriminator is stripped so callers can pass the payload
 * directly into helpers that take a plain `AuthConfig`.
 *
 * Actions are scanned both directly on the node and inside its condition
 * branches (`node.conditions[].actions`), since an auth sub-agent can be
 * configured under either. Requisite actions are text-only by type, so they
 * can't hold a sub_agent action.
 */
export const walkAuthSubAgentConfigs = (
  dialogs: MinimalDialog[] | undefined | null
): WalkedAuthConfig[] => {
  if (!Array.isArray(dialogs)) {
    return [];
  }
  const results: WalkedAuthConfig[] = [];

  const collect = (
    actions: Action[] | undefined,
    dialog: MinimalDialog,
    nodeId: string | undefined
  ) => {
    for (const action of actions ?? []) {
      if (
        action?.type !== 'sub_agent' ||
        (action.sub_agent_config as AuthSubAgentConfig | undefined)?.type !== 'auth'
      ) {
        continue;
      }
      const { type: _discriminator, ...config } = action.sub_agent_config as AuthSubAgentConfig;
      results.push({
        config,
        location: {
          dialog_id: dialog.dialog_id,
          dialog_name: dialog.name,
          node_id: nodeId,
          action_id: action.action_id,
        },
      });
    }
  };

  for (const dialog of dialogs) {
    const nodes = dialog?.nodes;
    if (!Array.isArray(nodes)) {
      continue;
    }
    for (const node of nodes) {
      collect(node?.actions as Action[], dialog, node?.node_id);
      for (const condition of node?.conditions ?? []) {
        collect(condition?.actions as Action[], dialog, node?.node_id);
      }
    }
  }
  return results;
};
